"use client";

import React from "react";
import { motion } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import Chip from "@/components/global/ui/Chip";
import Image from "@/helpers/Image";
import type { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import orangeBG from "@/public/home/orange-bg-daily-journal.jpg";
import DailyDiaryScene, { DailyDiarySceneMobile } from "./dailyDiary/DailyDiaryScene";

const TRANSITION_EASING = [0.22, 1, 0.36, 1] as const;
const VIEWPORT = { once: true, margin: "-50px" } as const;
const REVEAL_CHIP = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const REVEAL_TITLE = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const REVEAL_ITEM = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const REVEAL_CTA = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const CHIP_TRANSITION = { duration: 0.7, ease: [...TRANSITION_EASING] };
const TITLE_TRANSITION = { duration: 0.8, ease: [...TRANSITION_EASING], delay: 0.1 };
const CTA_TRANSITION = { duration: 0.7, ease: [...TRANSITION_EASING], delay: 0.2 };
const FEATURE_TRANSITION_BASE = { duration: 0.8, ease: [...TRANSITION_EASING] };

const FeatureBullet = (
  <svg
    width="9"
    height="9"
    viewBox="0 0 9 9"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <rect
      x="3"
      width="3"
      height="3"
      fill="#FF5500"
    />
    <rect
      x="6"
      y="3"
      width="3"
      height="3"
      fill="#FF5500"
    />
    <rect
      y="3"
      width="3"
      height="3"
      fill="#FF5500"
    />
    <rect
      x="3"
      y="6"
      width="3"
      height="3"
      fill="#FF5500"
    />
  </svg>
);

function DailyDiarySectionDesktop({ data }: { data: PageHomeSchema }) {
  const { dailyDiaryChip, dailyDiaryTitle, dailyDiaryCTA, dailyDiaryFeatures } = data;

  return (
    <section className="relative hidden w-full overflow-x-clip bg-[#040404] pt-[25rem] pb-[13rem] lg:block">
      <div className="mx-auto flex w-[min(180rem,100%)] flex-row gap-[12.5rem]">
        <div className="relative h-[104rem] w-[min(88rem,100%)] shrink-0 overflow-clip">
          <Image
            src={orangeBG}
            alt=""
            quality={100}
            className="absolute z-[1] h-full w-full object-cover"
          />
          <DailyDiaryScene className="z-[2]" />
        </div>

        <div className="flex flex-col justify-between">
          <div className="flex flex-col">
            {dailyDiaryChip && (
              <motion.div
                className="mb-[3rem] w-fit"
                {...REVEAL_CHIP}
                transition={CHIP_TRANSITION}>
                <Chip
                  animatedIcon
                  spinnerSize={32}
                  spinnerVariant="square"
                  text={dailyDiaryChip}
                  className="[&_*]:text-white"
                />
              </motion.div>
            )}

            {dailyDiaryTitle && (
              <motion.div
                className="richtext richtext-start w-[min(79rem,100%)] prose-h1:mb-[3rem] prose-h1:bg-gradient-to-b prose-h1:from-white prose-h1:to-[#CFCFCF] prose-h1:bg-clip-text prose-h1:text-transparent prose-p:text-white/80"
                {...REVEAL_TITLE}
                transition={TITLE_TRANSITION}>
                <PortableText
                  value={dailyDiaryTitle}
                  components={richTextComponent}
                />
              </motion.div>
            )}

            {dailyDiaryCTA && (
              <motion.div
                {...REVEAL_CTA}
                transition={CTA_TRANSITION}
                className="mt-[4rem]">
                <CTAButton data={dailyDiaryCTA} />
              </motion.div>
            )}
          </div>

          {dailyDiaryFeatures && dailyDiaryFeatures.length > 0 && (
            <div className="flex flex-col">
              <p className="font-spacemono text-[1.75rem] text-white/70 uppercase">Key features</p>
              {dailyDiaryFeatures.map((feature, index) => (
                <React.Fragment key={index}>
                  <motion.div
                    className="richtext richtext-start prose-p:m-0 prose-p:font-[400] prose-p:text-[2.25rem] prose-p:leading-[150%] prose-p:text-white/80"
                    {...REVEAL_ITEM}
                    transition={{ ...FEATURE_TRANSITION_BASE, delay: index * 0.1 }}>
                    <div className="flex items-start gap-[2rem]">
                      <div className="shrink-0 pt-[1rem]">{FeatureBullet}</div>
                      <PortableText
                        value={feature.text}
                        components={richTextComponent}
                      />
                    </div>
                  </motion.div>

                  {index < dailyDiaryFeatures.length - 1 && (
                    <hr className="my-[2.5rem] w-full border-t border-t-white/10" />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function DailyDiarySectionMobile({ data }: { data: PageHomeSchema }) {
  const { dailyDiaryChip, dailyDiaryTitle, dailyDiaryCTA, dailyDiaryFeatures } = data;

  return (
    <section className="relative w-full overflow-x-clip bg-[#040404] pt-[8.5rem] pb-[4rem] lg:hidden">
      <div className="mx-auto flex w-[min(180rem,100%)] flex-col gap-[12.5rem] px-[3rem]">
        <div className="flex flex-col justify-between">
          <div className="flex flex-col">
            {dailyDiaryChip && (
              <div className="mb-[3rem] w-fit">
                <Chip
                  spinnerSize={32}
                  spinnerVariant="square"
                  text={dailyDiaryChip}
                  className="[&_*]:text-white"
                />
              </div>
            )}

            {dailyDiaryTitle && (
              <div className="richtext richtext-start w-[min(79rem,100%)] prose-h1:mb-[3rem] prose-h1:bg-gradient-to-b prose-h1:from-white prose-h1:to-[#CFCFCF] prose-h1:bg-clip-text prose-h1:text-transparent prose-p:text-white/80">
                <PortableText
                  value={dailyDiaryTitle}
                  components={richTextComponent}
                />
              </div>
            )}

            <div className="relative my-[4rem] h-[56.72725rem] w-[min(88rem,100%)] shrink-0 overflow-clip">
              <Image
                src={orangeBG}
                alt=""
                quality={100}
                className="absolute z-[1] h-full w-full object-cover"
              />
              <DailyDiarySceneMobile className="z-[2]" />
            </div>
          </div>

          {dailyDiaryFeatures && dailyDiaryFeatures.length > 0 && (
            <div className="flex flex-col">
              <p className="font-spacemono text-[1.75rem] text-white/70 uppercase">Key features</p>
              {dailyDiaryFeatures.map((feature, index) => (
                <React.Fragment key={index}>
                  <div className="richtext richtext-start prose-p:m-0 prose-p:font-[400] prose-p:text-[2.25rem] prose-p:leading-[150%] prose-p:text-white/80">
                    <div className="flex items-start gap-[2rem]">
                      <div className="shrink-0 pt-[1rem]">{FeatureBullet}</div>
                      <PortableText
                        value={feature.text}
                        components={richTextComponent}
                      />
                    </div>
                  </div>

                  {index < dailyDiaryFeatures.length - 1 && (
                    <hr className="my-[2.5rem] w-full border-t border-t-white/10" />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {dailyDiaryCTA && (
            <div className="mt-[4rem]">
              <CTAButton data={dailyDiaryCTA} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default function DailyDiarySection({ data }: { data: PageHomeSchema }) {
  return (
    <>
      <DailyDiarySectionDesktop data={data} />
      <DailyDiarySectionMobile data={data} />
    </>
  );
}
